<template>
  <div ref="container" class="donut-container">
    <svg ref="svg" class="overflow-visible print:h-full print:w-full" />
  </div>
</template>

<script>
// https://observablehq.com/@d3/donut-chart
import * as d3 from "d3";
export default {
  name: "DonutChart",
  props: {
    data: {
      type: Array,
      default: () => [
        { name: "Val 1", value: 50, color: "#eeeeee" },
        { name: "Val 2", value: 100, color: "#cccccc" },
      ],
    },
    showLabels: {
      type: Boolean,
      default: true,
    },
    duration: {
      type: Number,
      default: 500,
    },
    minAngle: {
      type: Number,
      default: 0.3,
    },
    cornerRadius: {
      type: Number,
      default: 3,
    },
    padAngle: {
      type: Number,
      default: 0.02,
    },
  },
  data() {
    return {
      width: 200,
      height: 200,
      hoverIndex: null,
    };
  },
  computed: {
    pie() {
      return (
        d3
          .pie()
          .padAngle(this.padAngle)
          // .sort(null)
          .value((d) => d.value)
      );
    },
    arcs() {
      return this.pie(this.data);
    },
    arc() {
      const radius = Math.min(this.width, this.height) / 2;
      return d3
        .arc()
        .innerRadius(radius * 0.67)
        .outerRadius(radius - 1)
        .cornerRadius(this.cornerRadius);
    },
  },
  watch: {
    data() {
      this.updateChart();
    },
  },
  mounted() {
    this.setSize();
    this.createChart();
    window.addEventListener("resize", this.resize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resize);
  },
  methods: {
    setSize() {
      const container = this.$refs.container;
      if (container) {
        this.width = container.clientWidth;
        this.height = container.clientHeight;
      }
    },
    resize() {
      const currentWidth = this.width;
      const currentHeight = this.height;
      this.setSize();
      if (currentWidth !== this.width || currentHeight !== this.height) {
        this.updateChart();
      }
    },
    draw() {
      const that = this;

      function label(val) {
        return val.toFixed(1) + "%";
      }

      const displayLabel = (d) => d.endAngle - d.startAngle > this.minAngle;

      function arcTween(a) {
        var i = d3.interpolate(this._current, a);
        this._current = i(0);
        return function(t) {
          return that.arc(i(t));
        };
      }

      function textPositionTween(d) {
        this._current = this._current || d;
        var i = d3.interpolate(this._current, d);
        this._current = i(0);
        return function(t) {
          var d2 = i(t);
          var pos = that.arc.centroid(d2);
          return `translate(${pos})`;
        };
      }

      function textTween(d) {
        // we access the parentElement because we are tweening the tspan element but the data is in the text element
        var i = d3.interpolate(this.parentElement._current.value, d.value);
        this.parentElement._current.value = i(0);
        return function(t) {
          d3.select(this).text(label(i(t)));
        };
      }

      this.arcsPath = this.svg
        .selectAll("path")
        .data(this.arcs, (d) => d.data.name)
        .join(
          (enter) =>
            enter
              .append("path")
              .attr("fill", (d) => d.data.color)
              .each(function(d) {
                this._current = { ...d, endAngle: d.startAngle, value: 0 };
              })
              .attr("d", this.arc)

              .call((path) =>
                path
                  .transition()
                  .duration(this.duration)
                  // .delay(2*delay)
                  .attrTween("d", arcTween)
              ),
          // for accessibility
          // .append("title")
          // .text((d) => `${d.data.name}: ${d.data.value}`),
          (update) => {
            return update.call((path) =>
              path
                .transition()
                .duration(this.duration)
                // .attr("d", this.arc)
                .attrTween("d", arcTween)
            );
          },
          (exit) =>
            exit.call((path) =>
              path
                .each((path) => (path.endAngle = path.startAngle))
                .transition()
                .duration(this.duration)
                .attrTween("d", arcTween)
                .remove()
            )
        );

      this.textsContainer
        .raise()
        .selectAll("text")
        .data(this.arcs, (d) => d.data.name)
        .join(
          (enter) => {
            return enter
              .append("text")
              .attr("transform", (d) => `translate(${this.arc.centroid(d)})`)
              .attr("fill", (d) => d.data.textColor || "black")
              .attr("opacity", 0)
              .each(function(d) {
                this._current = { ...d, endAngle: d.startAngle, value: 0 };
              })
              .call((text) =>
                text
                  .append("tspan")
                  .attr("class", "label")
                  .attr("y", "-0.4em")
                  .attr("font-weight", "bold")
                  .text((d) => d.data.name)
                  .style("display", this.showLabels ? null : "none")
              )
              .call((text) =>
                text
                  .append("tspan")
                  .attr("class", "value")
                  .attr("x", 0)
                  .attr("y", "0.7em")
                  .attr("fill-opacity", 0.7)
                  .text(label(0))
                  .transition()
                  .duration(this.duration)
                  // .delay(2*delay)
                  .tween("text", textTween)
              )
              .call((text) =>
                text
                  .transition()
                  .duration(this.duration)
                  // .delay(2*delay)
                  .attrTween("transform", textPositionTween)
                  .attr("opacity", (d) => (displayLabel(d) ? 1 : 0))
              );
          },
          (update) => {
            return update.call((text) =>
              text
                .transition()
                .duration(this.duration)
                // .delay(delay)
                .attrTween("transform", textPositionTween)
                .attr("opacity", (d) => (displayLabel(d) ? 1 : 0))
                .select(".value")
                .tween("text", textTween)
            );
          },
          (exit) =>
            exit.call((text) =>
              text
                .each((text) => {
                  text.endAngle = text.startAngle;
                  text.value = 0;
                })
                .transition()
                .duration(this.duration)
                .attr("opacity", 0)
                .attrTween("transform", textPositionTween)
                .remove()
                .select(".value")
                .tween("text", textTween)
            )
        );
    },
    updateChart() {
      this.draw();
    },
    createChart() {
      if (!this.svg) {
        this.svg = d3
          .select(this.$refs.svg)
          .attr("viewBox", [
            -this.width / 2,
            -this.height / 2,
            this.width,
            this.height,
          ]);

        this.textsContainer = this.svg
          .append("g")
          .attr("font-family", "sans-serif")
          .attr("font-size", 12)
          .attr("text-anchor", "middle");

        this.draw();
      }
    },
  },
};
</script>

<style></style>
